import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Star, 
  MapPin, 
  Clock, 
  Phone,
  GraduationCap,
  Award,
  ChevronLeft,
  Calendar as CalendarIcon,
  Check,
  Loader2,
  Users,
  UserPlus,
  Video
} from 'lucide-react';
import { doctorService, appointmentService, patientService, getLocalizedValue } from '@/services/api';
import { format, startOfToday, addDays } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

// Helper function to get display name from doctor object
const getDoctorDisplayName = (doctor, lang) => {
  if (!doctor) return '';
  // If name is already a string, return it
  if (typeof doctor.name === 'string') return doctor.name;
  // If name is an object with lang keys
  if (doctor.name && typeof doctor.name === 'object') {
    return doctor.name[lang] || doctor.name['ar'] || doctor.name['en'] || '';
  }
  // Fallback to name_ar or name_en
  if (lang === 'ar' && doctor.name_ar) return doctor.name_ar;
  if (doctor.name_en) return doctor.name_en;
  return '';
};

// Helper to get specialty name
const getSpecialtyName = (doctor, lang) => {
  if (!doctor) return '';
  // Check specialties array first
  if (doctor.specialties && doctor.specialties.length > 0) {
    const spec = doctor.specialties[0];
    if (typeof spec.name === 'string') return spec.name;
    if (spec.name && typeof spec.name === 'object') {
      return spec.name[lang] || spec.name['ar'] || spec.name['en'] || '';
    }
  }
  // Check specialty object
  if (doctor.specialty) {
    if (typeof doctor.specialty.name === 'string') return doctor.specialty.name;
    if (doctor.specialty.name && typeof doctor.specialty.name === 'object') {
      return doctor.specialty.name[lang] || doctor.specialty.name['ar'] || doctor.specialty.name['en'] || '';
    }
  }
  return '';
};

const DoctorDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language || 'ar';
  
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(0); // Index of selected date
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [isOnlineBooking, setIsOnlineBooking] = useState(false); // Online or in-clinic appointment
  
  // Ref to prevent duplicate booking submissions
  const isBookingInProgress = useRef(false);
  
  // Patient selection for family member booking
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const locale = i18n.language === 'ar' ? ar : enUS;
  const isRTL = i18n.language === 'ar';

  // Generate next 7 days for date selection - memoized to prevent re-renders
  const today = React.useMemo(() => startOfToday(), []);
  const dateOptions = React.useMemo(() => 
    Array.from({ length: 7 }, (_, i) => addDays(today, i)), 
    [today]
  );
  const actualSelectedDate = dateOptions[selectedDate];
  
  // Create a stable date string for the useEffect dependency
  const selectedDateStr = format(actualSelectedDate, 'yyyy-MM-dd');

  useEffect(() => {
    const fetchDoctor = async () => {
      setLoading(true);
      try {
        const response = await doctorService.getById(id);
        const rawDoctor = response.data.data || response.data;
        
        // Transform doctor data to handle localized fields
        const transformedDoctor = {
          id: rawDoctor.id,
          name: getDoctorDisplayName(rawDoctor, i18n.language),
          name_ar: getDoctorDisplayName(rawDoctor, 'ar'),
          name_en: getDoctorDisplayName(rawDoctor, 'en'),
          specialty: {
            id: rawDoctor.specialties?.[0]?.id || rawDoctor.specialty?.id,
            name: getSpecialtyName(rawDoctor, i18n.language),
            name_ar: getSpecialtyName(rawDoctor, 'ar'),
            name_en: getSpecialtyName(rawDoctor, 'en'),
          },
          rating: rawDoctor.rate || rawDoctor.rating || 0,
          reviews_count: rawDoctor.total_reviews || rawDoctor.reviews_count || 0,
          experience_years: rawDoctor.experience_years || 10,
          address: rawDoctor.clinic ? getLocalizedValue(rawDoctor.clinic.name, i18n.language) : (rawDoctor.address || ''),
          phone: rawDoctor.clinic?.phone_number || rawDoctor.phone || '',
          email: rawDoctor.email || '',
          image: rawDoctor.media?.[0]?.url || rawDoctor.media?.[0]?.thumb || rawDoctor.image,
          // Keep raw description for HTML rendering
          bio: rawDoctor.description ? (typeof rawDoctor.description === 'object' 
            ? (rawDoctor.description[i18n.language] || rawDoctor.description['ar'] || rawDoctor.description['en'] || '')
            : rawDoctor.description) : (rawDoctor.bio || ''),
          bio_ar: rawDoctor.description ? (typeof rawDoctor.description === 'object'
            ? (rawDoctor.description['ar'] || '')
            : rawDoctor.description) : (rawDoctor.bio_ar || ''),
          consultation_fee: rawDoctor.price || rawDoctor.consultation_fee || 0,
          available: rawDoctor.available || false,
          languages: rawDoctor.languages || ['English'],
          clinic: rawDoctor.clinic ? {
            id: rawDoctor.clinic.id,
            name: getLocalizedValue(rawDoctor.clinic.name, i18n.language),
          } : null,
          // Store availability hours for slot generation
          availability_hours: rawDoctor.availability_hours || [],
          waiting_time: rawDoctor.waiting_time || 30, // Default 30 min slots
          // Online consultation support
          enable_online_consultation: rawDoctor.enable_online_consultation || false,
        };
        
        setDoctor(transformedDoctor);
      } catch (error) {
        console.error('Failed to fetch doctor:', error);
        // Set mock doctor data
        setDoctor({
          id: id,
          name: 'Dr. Sarah Johnson',
          name_ar: 'د. سارة جونسون',
          specialty: { id: 1, name: 'Cardiology', name_ar: 'أمراض القلب' },
          rating: 4.9,
          reviews_count: 127,
          experience_years: 15,
          address: 'Downtown Medical Center, 123 Medical Blvd, New York',
          phone: '+1 (555) 123-4567',
          email: 'dr.sarah@dactrha.com',
          image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=400&fit=crop',
          bio: 'Dr. Sarah Johnson is a board-certified cardiologist with over 15 years of experience in treating cardiovascular diseases. She specializes in preventive cardiology, heart failure management, and cardiac imaging.',
          bio_ar: 'الدكتورة سارة جونسون هي طبيبة قلب معتمدة مع أكثر من 15 عامًا من الخبرة في علاج أمراض القلب والأوعية الدموية.',
          consultation_fee: 150,
          available: true,
          languages: ['English', 'Spanish'],
          availability_hours: [],
          waiting_time: 30,
          enable_online_consultation: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDoctor();
  }, [id, i18n.language]);

  // Fetch patient profiles (family members) for the logged in user
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    
    const fetchPatients = async () => {
      setPatientsLoading(true);
      try {
        const response = await patientService.getByUserId(user.id);
        const patientsData = response.data?.data || response.data || [];
        
        if (Array.isArray(patientsData) && patientsData.length > 0) {
          setPatients(patientsData);
          // Select default patient or first one
          const defaultPatient = patientsData.find(p => p.is_default) || patientsData[0];
          setSelectedPatient(defaultPatient);
        }
      } catch (error) {
        console.error('Failed to fetch patients:', error);
      } finally {
        setPatientsLoading(false);
      }
    };
    
    fetchPatients();
  }, [isAuthenticated, user?.id]);

  // Fetch slots from availability_hours API based on selected date
  useEffect(() => {
    if (!doctor || !id) return;
    
    const fetchSlots = async () => {
      setSlotsLoading(true);
      setSelectedSlot(null);
      
      try {
        // Format date as YYYY-MM-DD for the API
        const dateStr = format(actualSelectedDate, 'yyyy-MM-dd');
        
        // Call the availability_hours API endpoint
        const response = await doctorService.getAvailabilityHours(id, dateStr);
        const slotsData = response.data?.data || response.data || [];
        
        if (slotsData.length > 0) {
          // Transform API response to slot objects
          // API format: [[datetime, isAvailable, isBooked], ...]
          const slots = slotsData.map(slot => {
            const dateTime = new Date(slot[0]);
            const isAvailable = slot[1];
            const isBooked = slot[2];
            
            // Format time as 12-hour format
            const hour = dateTime.getHours();
            const minute = dateTime.getMinutes();
            const hour12 = hour % 12 || 12;
            const ampm = hour < 12 ? 'AM' : 'PM';
            const formattedMinute = String(minute).padStart(2, '0');
            const timeStr = `${hour12}:${formattedMinute} ${ampm}`;
            
            return {
              time: timeStr,
              datetime: slot[0],
              available: isAvailable && !isBooked, // Available only if isAvailable=true AND isBooked=false
              isBooked: isBooked,
            };
          });
          
          setAvailableSlots(slots);
        } else {
          // No slots available for this day
          setAvailableSlots([]);
        }
      } catch (error) {
        console.error('Failed to fetch availability hours:', error);
        // Fallback to client-side slot generation if API fails
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const selectedDayIndex = actualSelectedDate.getDay();
        const selectedDayName = dayNames[selectedDayIndex];
        
        const dayAvailability = doctor.availability_hours?.find(h => h.day === selectedDayName);
        
        if (dayAvailability) {
          const slots = [];
          const startParts = dayAvailability.start_at.split(':');
          const endParts = dayAvailability.end_at.split(':');
          const interval = parseInt(doctor.waiting_time) || 30;
          
          let currentHour = parseInt(startParts[0], 10);
          let currentMinute = parseInt(startParts[1], 10);
          const endHour = parseInt(endParts[0], 10);
          const endMinute = parseInt(endParts[1], 10);
          
          while (currentHour < endHour || (currentHour === endHour && currentMinute < endMinute)) {
            const hour12 = currentHour % 12 || 12;
            const ampm = currentHour < 12 ? 'AM' : 'PM';
            const formattedMinute = String(currentMinute).padStart(2, '0');
            const timeStr = `${hour12}:${formattedMinute} ${ampm}`;
            
            slots.push({
              time: timeStr,
              available: true,
              isBooked: false,
            });
            
            currentMinute = currentMinute + interval;
            if (currentMinute >= 60) {
              currentHour = currentHour + Math.floor(currentMinute / 60);
              currentMinute = currentMinute % 60;
            }
          }
          
          setAvailableSlots(slots);
        } else {
          setAvailableSlots([]);
        }
      } finally {
        setSlotsLoading(false);
      }
    };
    
    fetchSlots();
  }, [doctor, id, selectedDateStr, actualSelectedDate]);

  const handleBookAppointment = async () => {
    // CRITICAL: Prevent duplicate submissions using ref (survives re-renders)
    if (isBookingInProgress.current) {
      console.log('Booking already in progress, ignoring duplicate click');
      return;
    }
    
    if (!isAuthenticated) {
      toast.info(t('common.login'), { description: 'Please login to book an appointment' });
      navigate('/login');
      return;
    }

    if (!selectedSlot) {
      toast.error(t('common.error'), { description: 'Please select a time slot' });
      return;
    }

    // Set ref BEFORE state to prevent any race conditions
    isBookingInProgress.current = true;
    setBookingLoading(true);
    
    try {
      // Build the appointment payload to match Flutter mobile app structure
      // The backend expects full doctor, patient objects AND user_id at root level
      
      // Parse the selected time to create a proper datetime
      const appointmentDateTime = new Date(actualSelectedDate);
      const timeParts = selectedSlot.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (timeParts) {
        let hours = parseInt(timeParts[1]);
        const minutes = parseInt(timeParts[2]);
        const period = timeParts[3]?.toUpperCase();
        
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
        
        appointmentDateTime.setHours(hours, minutes, 0, 0);
      }
      
      // Calculate end time (add session duration or default 30 minutes)
      const sessionDuration = doctor?.session_duration || doctor?.waiting_time || 30;
      const endDateTime = new Date(appointmentDateTime.getTime() + sessionDuration * 60000);
      
      // Get patient - use selected patient from dropdown or fallback to user's default
      const patient = selectedPatient || patients[0];
      const patientId = patient?.id || user?.patient_id || user?.default_patient_id;
      
      if (!patientId) {
        toast.error(
          lang === 'ar' ? 'يرجى إنشاء ملف مريض أولاً' : 'Please create a patient profile first',
          { description: lang === 'ar' ? 'انتقل إلى أفراد العائلة لإضافة مريض' : 'Go to Family Members to add a patient' }
        );
        setBookingLoading(false);
        return;
      }
      
      // Build the booking payload matching Flutter app structure + Laravel API expectations
      const bookingPayload = {
        // User ID at root level (REQUIRED by Laravel API)
        user_id: user?.id,
        // Doctor object (required by backend)
        doctor: {
          id: doctor?.id || id,
          name: typeof doctor?.name === 'object' ? (doctor.name[lang] || doctor.name.ar || doctor.name.en || '') : (doctor?.name || ''),
          price: doctor?.price || doctor?.consultation_fee || 0,
          available: doctor?.available ?? true,
          clinic_id: doctor?.clinic?.id || null,
        },
        // Patient object (required by backend) - use selected patient details
        patient: {
          id: patientId,
          user_id: user?.id,
          first_name: getLocalizedValue(patient?.first_name, lang) || patient?.first_name?.en || user?.first_name || user?.name?.split(' ')[0] || '',
          last_name: getLocalizedValue(patient?.last_name, lang) || patient?.last_name?.en || user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
          phone_number: patient?.phone_number || patient?.mobile_number || user?.phone_number || user?.phone || '',
        },
        // Clinic object (if doctor has a clinic)
        ...(doctor?.clinic && {
          clinic: {
            id: doctor.clinic.id,
            name: typeof doctor.clinic.name === 'object' 
              ? (doctor.clinic.name[lang] || doctor.clinic.name.ar || doctor.clinic.name.en) 
              : (doctor.clinic.name || ''),
          }
        }),
        // Appointment times - format: "YYYY-MM-DD HH:MM:SS"
        appointment_at: appointmentDateTime.toISOString().replace('T', ' ').substring(0, 19),
        start_at: appointmentDateTime.toISOString().replace('T', ' ').substring(0, 19),
        ends_at: endDateTime.toISOString().replace('T', ' ').substring(0, 19),
        // Online or at clinic
        online: isOnlineBooking,
        // Empty hint/notes for direct booking
        hint: '',
      };
      
      console.log('Booking payload:', bookingPayload);
      
      await appointmentService.book(bookingPayload);
      toast.success(t('common.success'), { description: 'Appointment booked successfully!' });
      navigate('/appointments');
    } catch (error) {
      console.error('Booking error:', error.response?.data || error);
      const errorMessage = error.response?.data?.message || 'Failed to book appointment';
      
      // Provide more helpful error messages based on backend response
      let userFriendlyMessage = errorMessage;
      if (errorMessage.includes('already has an appointment') || errorMessage.includes('selected time')) {
        userFriendlyMessage = lang === 'ar' 
          ? 'لديك موعد آخر في هذا الوقت. يرجى اختيار وقت مختلف.'
          : 'You already have another appointment at this time. Please choose a different time slot.';
      } else if (errorMessage.includes('Undefined array key')) {
        userFriendlyMessage = lang === 'ar'
          ? 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى.'
          : 'A server error occurred. Please try again later.';
      }
      
      toast.error(t('common.error'), { description: userFriendlyMessage });
    } finally {
      setBookingLoading(false);
      // Reset ref after a short delay to allow state to settle
      setTimeout(() => {
        isBookingInProgress.current = false;
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-detail-loading">
        <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
          <div className="container mx-auto px-4 py-6">
            <Skeleton className="h-8 w-32 mb-4" />
            <div className="flex gap-6">
              <Skeleton className="h-32 w-32 rounded-xl" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-6 w-1/3" />
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-detail-page">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <Button variant="ghost" size="sm" className="mb-4 -ml-2" onClick={() => navigate(-1)} data-testid="back-button">
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('common.back')}
          </Button>

          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="h-32 w-32 rounded-xl">
              <AvatarImage src={doctor.image || `https://ui-avatars.com/api/?name=${doctor.name}&background=0F6D77&color=fff`} alt={doctor.name} className="object-cover" />
              <AvatarFallback className="rounded-xl bg-teal-600 text-white text-2xl">{doctor.name?.charAt(0)}</AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-1" data-testid="doctor-name">
                    {isRTL && doctor.name_ar ? doctor.name_ar : doctor.name}
                  </h1>
                  <p className="text-teal-600 font-medium mb-2">
                    {isRTL && doctor.specialty?.name_ar ? doctor.specialty.name_ar : doctor.specialty?.name}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold">{doctor.rating}</span>
                      <span className="text-slate-400">({doctor.reviews_count} {t('doctors.reviews')})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span>{doctor.experience_years} {t('doctors.years')} {t('doctors.experience')}</span>
                    </div>
                  </div>
                </div>
                {doctor.available ? (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Available</Badge>
                ) : (
                  <Badge variant="secondary">Not Available</Badge>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-400">
                {doctor.address && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{doctor.address}</span>
                  </div>
                )}
                {doctor.phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{doctor.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Doctor Details */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="about">About</TabsTrigger>
                <TabsTrigger value="education">{t('doctors.education')}</TabsTrigger>
                <TabsTrigger value="reviews">{t('doctors.reviews')}</TabsTrigger>
              </TabsList>

              <TabsContent value="about" className="mt-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">About Doctor</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Render bio as HTML since API returns HTML content */}
                    <div 
                      className="text-slate-600 leading-relaxed prose prose-slate max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: isRTL && doctor.bio_ar ? doctor.bio_ar : doctor.bio 
                      }}
                    />
                    {doctor.languages && doctor.languages.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-medium text-slate-900 mb-2">Languages</h4>
                        <div className="flex flex-wrap gap-2">
                          {doctor.languages.map((lang, idx) => (
                            <Badge key={idx} variant="secondary">{lang}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="education" className="mt-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('doctors.education')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3 pb-4 border-b">
                      <div className="p-2 rounded-lg bg-teal-50">
                        <GraduationCap className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">MD in Cardiology</h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Harvard Medical School</p>
                        <p className="text-xs text-slate-400">2005</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-teal-50">
                        <GraduationCap className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">Fellowship in Interventional Cardiology</h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Johns Hopkins Hospital</p>
                        <p className="text-xs text-slate-400">2008</p>
                      </div>
                    </div>
                    <div className="mt-6 pt-4 border-t">
                      <h4 className="font-medium text-slate-900 mb-4">Awards & Recognition</h4>
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-amber-50">
                          <Award className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">Best Cardiologist Award</h4>
                          <p className="text-xs text-slate-400">2020</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-amber-50">
                          <Award className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">Excellence in Patient Care</h4>
                          <p className="text-xs text-slate-400">2019</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="reviews" className="mt-4">
                <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Patient Reviews</CardTitle>
                    <CardDescription>Based on {doctor.reviews_count} reviews</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-slate-900 dark:text-white">{doctor.rating}</div>
                        <div className="flex items-center justify-center mt-1">
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                          <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        </div>
                      </div>
                      <div className="flex-1 text-sm text-slate-600 dark:text-slate-400">
                        <p>{doctor.reviews_count} patients have reviewed this doctor</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          </div>
                          <span className="text-sm text-slate-500 dark:text-slate-400">2 days ago</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Excellent doctor! Very thorough and took time to explain everything clearly.</p>
                        <p className="text-xs text-slate-400 mt-2">- John D.</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            <Star className="h-3 w-3 text-slate-300" />
                          </div>
                          <span className="text-sm text-slate-500 dark:text-slate-400">1 week ago</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Professional and caring. The wait time was a bit long but the consultation was worth it.</p>
                        <p className="text-xs text-slate-400 mt-2">- Sarah M.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Booking */}
          <div className="space-y-6">
            <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Consultation Fee</span>
                  <span className="text-2xl font-bold text-teal-600">${doctor.consultation_fee || 150}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 border-0 shadow-sm sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  {t('appointments.bookNew')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">{t('appointments.selectDate')}</label>
                  <div className="flex flex-wrap gap-2">
                    {dateOptions.map((date, idx) => (
                      <Button
                        key={idx}
                        variant={selectedDate === idx ? "default" : "outline"}
                        size="sm"
                        className={selectedDate === idx ? 'bg-teal-600 hover:bg-teal-700' : ''}
                        onClick={() => { setSelectedDate(idx); setSelectedSlot(null); }}
                        data-testid={`date-option-${idx}`}
                      >
                        <div className="text-center">
                          <div className="text-xs">{format(date, 'EEE', { locale })}</div>
                          <div className="text-sm font-bold">{format(date, 'd', { locale })}</div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('appointments.selectTime')} - {format(actualSelectedDate, 'EEE, MMM d', { locale })}
                  </label>
                  {slotsLoading ? (
                    <div className="grid grid-cols-3 gap-2">
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                      <Skeleton className="h-10" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">{t('doctors.noSlotsAvailable')}</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map((slot, idx) => (
                        <Button
                          key={idx}
                          variant={selectedSlot?.time === slot.time ? "default" : "outline"}
                          size="sm"
                          className={`relative ${selectedSlot?.time === slot.time ? 'bg-teal-600 hover:bg-teal-700' : ''} ${!slot.available ? 'opacity-50 cursor-not-allowed bg-slate-100 border-slate-200 text-slate-400' : ''}`}
                          disabled={!slot.available}
                          onClick={() => slot.available && setSelectedSlot(slot)}
                          data-testid={`time-slot-${idx}`}
                          title={slot.isBooked ? (lang === 'ar' ? 'محجوز' : 'Booked') : ''}
                        >
                          <span className={slot.isBooked ? 'line-through' : ''}>{slot.time}</span>
                          {slot.isBooked && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] px-1 rounded">
                              {lang === 'ar' ? 'محجوز' : 'Booked'}
                            </span>
                          )}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedSlot && (
                  <div className="p-3 bg-teal-50 dark:bg-teal-900/30 rounded-lg border border-teal-100 dark:border-teal-800">
                    <div className="flex items-center gap-2 text-sm text-teal-700 dark:text-teal-300">
                      <Check className="h-4 w-4" />
                      <span>{format(actualSelectedDate, 'EEEE, MMMM d', { locale })} at {selectedSlot.time}</span>
                    </div>
                  </div>
                )}

                {/* Appointment Type - Online or In-Clinic */}
                {doctor?.enable_online_consultation && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      {lang === 'ar' ? 'نوع الموعد' : 'Appointment Type'}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant={!isOnlineBooking ? "default" : "outline"}
                        className={!isOnlineBooking ? "bg-teal-600 hover:bg-teal-700" : ""}
                        onClick={() => setIsOnlineBooking(false)}
                        data-testid="btn-in-clinic"
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        {lang === 'ar' ? 'في العيادة' : 'In Clinic'}
                      </Button>
                      <Button
                        type="button"
                        variant={isOnlineBooking ? "default" : "outline"}
                        className={isOnlineBooking ? "bg-teal-600 hover:bg-teal-700" : ""}
                        onClick={() => setIsOnlineBooking(true)}
                        data-testid="btn-online"
                      >
                        <Video className="h-4 w-4 mr-2" />
                        {lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation'}
                      </Button>
                    </div>
                    {isOnlineBooking && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                        <Video className="h-3 w-3" />
                        {lang === 'ar' 
                          ? 'ستتمكن من إجراء مكالمة فيديو مع الطبيب في الموعد المحدد'
                          : 'You can video call the doctor at the scheduled time'}
                      </p>
                    )}
                  </div>
                )}

                {/* Patient Selection - Book for self or family member */}
                {isAuthenticated && (
                  <div>
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {lang === 'ar' ? 'حجز لـ' : 'Book for'}
                    </label>
                    {patientsLoading ? (
                      <Skeleton className="h-10 w-full" />
                    ) : patients.length === 0 ? (
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                        <span className="text-sm text-slate-600 dark:text-slate-300">
                          {lang === 'ar' ? 'لا يوجد ملف مريض' : 'No patient profile'}
                        </span>
                        <Button variant="outline" size="sm" asChild>
                          <Link to="/family" className="flex items-center gap-1">
                            <UserPlus className="h-3 w-3" />
                            {lang === 'ar' ? 'إضافة' : 'Add'}
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <Select 
                        value={selectedPatient?.id?.toString()} 
                        onValueChange={(value) => {
                          const patient = patients.find(p => p.id?.toString() === value);
                          setSelectedPatient(patient);
                        }}
                      >
                        <SelectTrigger 
                          className="dark:bg-slate-700 dark:text-white"
                          data-testid="patient-selector"
                        >
                          <SelectValue placeholder={lang === 'ar' ? 'اختر المريض' : 'Select patient'} />
                        </SelectTrigger>
                        <SelectContent>
                          {patients.map(patient => {
                            const firstName = getLocalizedValue(patient.first_name, lang) || patient.first_name?.en || patient.first_name || '';
                            const lastName = getLocalizedValue(patient.last_name, lang) || patient.last_name?.en || patient.last_name || '';
                            const name = `${firstName} ${lastName}`.trim();
                            return (
                              <SelectItem key={patient.id} value={patient.id?.toString()}>
                                <div className="flex items-center gap-2">
                                  <span>{name || `Patient ${patient.id}`}</span>
                                  {patient.is_default && (
                                    <Badge variant="secondary" className="text-xs">
                                      {lang === 'ar' ? 'افتراضي' : 'Default'}
                                    </Badge>
                                  )}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  size="lg"
                  disabled={!selectedSlot || bookingLoading}
                  onClick={handleBookAppointment}
                  data-testid="book-appointment-button"
                >
                  {bookingLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {t('appointments.confirmBooking')}
                    </>
                  )}
                </Button>

                {!isAuthenticated && (
                  <p className="text-xs text-center text-slate-500 dark:text-slate-400">
                    Please <Link to="/login" className="text-teal-600 hover:underline">{t('common.login')}</Link> to book an appointment
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDetailPage;
